import './LiveMediaStream.sass';

import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { liveActions, liveMediaStreamSelector, liveSelector } from '$store/live';
import { LiveMediaStreamProps } from '$core/Live/LiveMediaStream/LiveMediaStream.interface';
import { classNames as cn } from '$utils';
import { Loader } from '$shared/components';

export const LiveMediaStream: FC<LiveMediaStreamProps> = ({
  content,
  children,
  blur = false,
  solid = false,
  selfie = false,
  cover,
  muted,
}) => {
  const mediaStream = useSelector(liveMediaStreamSelector);
  const video = useRef<HTMLVideoElement>(null);
  const paintedFrames = useRef<number>(0); // for FF
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    if (video.current) {
      video.current.srcObject = mediaStream;
      video.current.play && video.current.play()?.catch(() => {});
    }
  }, [video.current, mediaStream]);

  useEffect(() => {
    if (video.current) {
      video.current.muted = !!muted;
    }
  }, [video.current, muted]);

  useEffect(() => {
    if (video.current && 'requestVideoFrameCallback' in HTMLVideoElement.prototype) {
      const checkFrame = () => {
        let timeout = setTimeout(() => {
          setLoader(true);
        }, 500);
        // @ts-ignore
        video.current?.requestVideoFrameCallback(() => {
          setLoader(false);
          clearTimeout(timeout);
          checkFrame();
          video?.current?.play && video.current.play()?.catch(() => {});
        });
      };
      checkFrame();
    } else {
      const interval = setInterval(() => {
        if (mediaStream) {
          const [track] = mediaStream.getVideoTracks();
          if (!track) return;
          const { frameRate } = track.getSettings();
          // for Firefox
          // @ts-ignore
          const { mozPaintedFrames } = video.current;
          if (frameRate) {
            setLoader(!frameRate || frameRate === 0);
          } else if (mozPaintedFrames) {
            setLoader(mozPaintedFrames === paintedFrames.current);
            paintedFrames.current = mozPaintedFrames;
          }

          video?.current?.play && video.current.play()?.catch(() => {});
        }
      }, 250);
      return () => clearInterval(interval);
    }
  }, [video.current, mediaStream]);

  const body = content || children;

  return (
    <div
      className={cn('LiveMediaStream', {
        selfie,
      })}
      style={
        cover
          ? {
              backgroundImage: `url(${cover}`,
            }
          : {}
      }
    >
      <video ref={video} muted={true} autoPlay playsInline controls={false} />
      {body && (
        <div
          className={cn('LiveMediaStream__content', {
            blur: blur || !!cover,
            solid,
          })}
        >
          {body}
        </div>
      )}
      {loader && !body && (
        <div className="LiveMediaStream__loader">
          <Loader />
        </div>
      )}
    </div>
  );
};
